import React from "react"
import { useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { cn } from "~/ui-rtk/utils/tailwind-utils"

interface SortableMetricRowProps {
  id: string
  label: string
  category: string
  isWowGrowth: boolean
  onToggleWowGrowth: () => void
  onRemove: () => void
}

// A single selected metric inside the group editor: drag handle to reorder,
// a period-over-period growth toggle, and a remove button. The drag listeners
// are scoped to the handle so the toggle/remove controls stay clickable.
const SortableMetricRow: React.FC<SortableMetricRowProps> = ({
  id,
  label,
  category,
  isWowGrowth,
  onToggleWowGrowth,
  onRemove,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex items-center justify-between gap-3 p-3 bg-system-blue-800 border border-system-blue-600 rounded-system-md",
        isDragging && "opacity-60 shadow-xl z-50 border-system-blue-500",
      )}
    >
      <div className="flex items-center gap-3 min-w-0">
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="flex items-center justify-center w-6 h-6 bg-system-blue-600 rounded-system-xs cursor-grab active:cursor-grabbing flex-shrink-0"
          title="Drag to reorder"
        >
          <svg
            className="w-3 h-3 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 8h16M4 16h16"
            />
          </svg>
        </button>
        <div className="min-w-0">
          <p className="text-white text-sm font-medium truncate">{label}</p>
          <p className="text-system-blue-300 text-xs truncate">{category}</p>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-shrink-0">
        <label
          className="flex items-center gap-1.5 text-xs text-system-blue-200 cursor-pointer select-none"
          title="Show a period-over-period growth row beneath this metric (day/week/month, per the selected time grouping)"
        >
          <input
            type="checkbox"
            checked={isWowGrowth}
            onChange={onToggleWowGrowth}
            className="checkbox checkbox-xs"
          />
          PoP Growth
        </label>
        <button
          type="button"
          onClick={onRemove}
          className="text-system-blue-300 hover:text-system-error-400 transition-colors"
          title="Remove metric"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
    </div>
  )
}

export default SortableMetricRow
